import { createStore, del, get, set, type UseStore } from 'idb-keyval';

/**
 * IndexedDB-backed storage adapter for TanStack Query's
 * `createAsyncStoragePersister` (Batch 2 Part B, 12 Jun 2026).
 *
 * Why this exists:
 *   Caravan-park wifi guests close their tab and reopen later. Without
 *   persistence, every reopen is a cold start — chat history bubbles
 *   show a loading state until /v1/chat/history resolves over patchy
 *   site wifi (2–5s realistic). With persistence, the bubbles render
 *   instantly from IDB while the network reconciles in the background.
 *
 * Why IndexedDB, not localStorage:
 *   - localStorage is synchronous and blocks the main thread on writes;
 *     IDB is async and off-main-thread
 *   - localStorage caps at ~5MB per origin; chat history could push
 *     that on a long stay, especially with rich text or future
 *     attachment URLs
 *   - IDB shares the broader PWA storage quota, which iOS Safari
 *     measures generously when the app is installed to home screen
 *
 * Why idb-keyval, not a hand-rolled IDB wrapper:
 *   - ~600 bytes minified, zero transitive deps
 *   - Mature, widely deployed (including by other TanStack Query
 *     consumers in the wild)
 *   - Promise-based API matches the AsyncStorage contract exactly,
 *     so the adapter below is a thin pass-through
 *
 * Failure mode:
 *   If IDB is unavailable (private mode on some browsers, quota
 *   exceeded, OS-level disk pressure), every method silently resolves
 *   to null / void. The persister falls back to in-memory cache, which
 *   is the same behaviour as today's pre-Batch-2 production. No
 *   user-visible error, no Sentry spam, no degraded UX beyond losing
 *   the instant-render benefit.
 */

const DB_NAME = 'towninsider-cache';
const STORE_NAME = 'react-query';
const CACHE_KEY = 'dehydrated-state';

/**
 * Lazy store creation. `createStore` opens the IDB database on first
 * call; defer that to first read/write so module evaluation stays
 * synchronous and SSR / test environments without IDB don't throw at
 * import time. The store handle is cached after first creation.
 */
let store: UseStore | null = null;
function getStore(): UseStore {
  if (store === null) {
    store = createStore(DB_NAME, STORE_NAME);
  }
  return store;
}

/**
 * AsyncStorage-compatible adapter for `createAsyncStoragePersister`.
 *
 * Contract (per @tanstack/query-async-storage-persister):
 *   getItem(key)   → Promise<string | null>
 *   setItem(k, v)  → Promise<void>
 *   removeItem(k)  → Promise<void>
 *
 * Every method swallows IDB failures and degrades silently to the
 * in-memory cache. Errors are intentionally not reported to Sentry —
 * IDB unavailability is a known environmental condition (private
 * browsing, quota pressure), not an application bug.
 */
export const persisterStorage = {
  async getItem(key: string): Promise<string | null> {
    try {
      const value = await get<string>(key, getStore());
      return value ?? null;
    } catch {
      return null;
    }
  },
  async setItem(key: string, value: string): Promise<void> {
    try {
      await set(key, value, getStore());
    } catch {
      // ignore — silent degradation to in-memory cache
    }
  },
  async removeItem(key: string): Promise<void> {
    try {
      await del(key, getStore());
    } catch {
      // ignore
    }
  },
};

/**
 * The key under which the dehydrated TanStack Query cache is written.
 *
 * Single fixed key by design. Per-user cache isolation is enforced at
 * the query-key level (Part A, 11 Jun 2026 — `['chat', 'history',
 * userId]`) and at the dehydrate filter in `main.tsx` (which rejects
 * any query whose userId segment is null / empty). Adding a per-user
 * IDB key on top would require rebuilding the persister whenever Clerk
 * resolves a new userId, which `PersistQueryClientProvider` does not
 * easily support — and would buy nothing, because account B literally
 * cannot read account A's persisted entries by query-key even if they
 * share the IDB key.
 */
export const PERSISTER_CACHE_KEY = CACHE_KEY;

/**
 * Wipe the persisted query cache. Called from `clearAllAppStorage` on
 * sign-out so account A's persisted chat history doesn't sit on disk
 * after they hand the device to account B.
 *
 * Fire-and-forget from the caller's perspective (`void
 * wipePersistedQueryCache()`). The race window — sign-out completes
 * before this wipe finishes — is structurally harmless because:
 *
 *   1. Every persisted query key includes the owning userId (Part A,
 *      11 Jun 2026 — `['chat', 'history', userId]`). Account B can
 *      never read account A's entries by key.
 *
 *   2. The dehydrate filter on the persister (see `main.tsx`) excludes
 *      any query whose last key segment is null / undefined / empty,
 *      so the resolving-Clerk window can't persist a null-keyed entry
 *      that would later collide.
 *
 * This wipe is defence-in-depth, not load-bearing. It prevents
 * unbounded IDB growth on shared devices and gives a clean
 * "factory reset" on sign-out.
 */
export async function wipePersistedQueryCache(): Promise<void> {
  try {
    await del(CACHE_KEY, getStore());
  } catch {
    // ignore — same silent-degradation policy as the adapter methods
  }
}
